import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { User, Users, TrendingUp, Crown, RefreshCw, Timer, ChevronLeft } from 'lucide-react';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';

// Create a context to track node positions globally within the tree
const TreeContext = createContext(null);

// Helper to map API response to binary tree format
const mapResponseToTree = (node) => {
    if (!node) return null;
    const mapped = {
        id: node.id,
        name: node.name,
        user_id: node.user_id || node.user_identity_id || '',
        referral_code: node.referral_code || '',
        pv: node.user_pv_points || node.pv_points || node.pv || 0,
        position: node.position,
        joined_at: node.joined_at,
        activated_at: node.activated_at,
        package: node.package || node.package_name || 'Starter',
        sponsor_id: node.sponsor_user_id || 'N/A',
        wallet_balance: node.wallet_balance || 0,
        left_count: node.left_count || 0,
        right_count: node.right_count || 0,
        left_pv: node.left_pv || 0,
        right_pv: node.right_pv || 0,
        user_status: node.user_status || 'active',
        is_root: node.is_root || false,
        left: null,
        right: null
    };

    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        const getPos = (c) => String(c?.position ?? '').trim().toLowerCase();
        let leftNode = node.children.find(c => getPos(c) === 'l' || getPos(c) === 'left');
        let rightNode = node.children.find(c => getPos(c) === 'r' || getPos(c) === 'right');

        // Only fallback to index-based mapping if NO explicit positions are found
        // This prevents a single "Right" child from being mapped to "Left" slot
        if (!leftNode && !rightNode) {
            leftNode = node.children[0];
            rightNode = node.children[1];
        }

        mapped.left = mapResponseToTree(leftNode);
        mapped.right = mapResponseToTree(rightNode);
    } else if (node.left || node.right) {
        mapped.left = mapResponseToTree(node.left);
        mapped.right = mapResponseToTree(node.right);
    }
    return mapped;
};

// Get initials from name
const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

// ==================== USER CARD (Clean - no package, no earnings) ====================
const UserCard = ({ user, isRoot = false, position, side, cardRef, onUserClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });

    const handleMouseEnter = () => {
        if (cardRef?.current) {
            const rect = cardRef.current.getBoundingClientRect();
            let left = rect.right + 8;
            let top = rect.top;
            setTooltipPos({ left, top });
        }
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    if (!user) return null;

    const sideLower = String(side || position).toLowerCase();
    const isLeft = sideLower === 'left' || sideLower === 'l';
    const isRight = sideLower === 'right' || sideLower === 'r';

    const isInactive = !user.package || user.package === 'None' || user.package === 'STARTER MEMBER' || Number(user.pv || 0) <= 0;


    const handleClick = () => {
        if (onUserClick && !isRoot) {
            onUserClick(user);
        }
    };

    return (
        <div
            ref={cardRef}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 relative cursor-pointer"
            style={{ width: '210px' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            {/* Premium Package Header */}
            <div className={`px-4 py-2.5 flex items-center justify-between ${isInactive ? 'bg-red-600' : isRoot ? 'bg-amber-500' : isLeft ? 'bg-blue-600' : isRight ? 'bg-purple-600' : 'bg-gray-600'}`}>
                <span className="text-[9px] font-black uppercase tracking-[0.15em] text-white/95 truncate pr-2">{user.package || 'STARTER'}</span>
                <div className="flex items-center">
                    <div className={`w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_10px_rgba(255,255,255,1)] ${isInactive ? 'bg-white/40' : 'animate-pulse'}`}></div>
                </div>
            </div>

            {/* User Info */}
            <div className="px-4 py-4 flex items-center gap-3">
                {/* Avatar */}
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-black shrink-0 shadow-lg ${isInactive ? 'bg-gradient-to-br from-red-500 to-red-600' : isRoot ? 'bg-gradient-to-br from-amber-400 to-amber-500' : isLeft ? 'bg-gradient-to-br from-blue-500 to-blue-600' : isRight ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
                    {getInitials(user.name)}
                </div>
                {/* Name & Username */}
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-black text-gray-900 truncate leading-tight">{user.name || 'Unknown'}</p>
                    <p className="text-[10px] font-bold text-gray-400/80 truncate mt-0.5">{user.referral_code || user.user_id || '---'}</p>
                </div>
                {isRoot && <Crown size={16} className="text-amber-500 shrink-0 filter drop-shadow-sm" />}
            </div>

            {/* Subtle Separator */}
            <div className="mx-4 border-t border-gray-50"></div>

            {/* PV Row */}
            <div className="px-4 py-3 flex justify-between items-center bg-gray-50/30">
                <div className="flex items-center gap-1.5">
                    <span className="text-[8px] text-gray-400 font-black uppercase tracking-wider">PV:</span>
                    <span className={`text-[13px] font-black ${isInactive ? 'text-red-600' : isRoot ? 'text-amber-600' : isLeft ? 'text-blue-600' : isRight ? 'text-purple-600' : 'text-emerald-600'}`}>
                        {Number(user.pv || 0).toLocaleString()}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-[9px] font-bold text-gray-400">
                    <span className="bg-gray-100/80 px-1.5 py-0.5 rounded">L: {user.left_count || 0}</span>
                    <span className="bg-gray-100/80 px-1.5 py-0.5 rounded">R: {user.right_count || 0}</span>
                </div>
            </div>

            {/* Drill-down hint */}
            {!isRoot && (user.left_count > 0 || user.right_count > 0) && (
                <div className="px-3 pb-2 flex justify-end">
                    <div className="flex items-center gap-0.5 text-[8px] font-extrabold text-blue-500 uppercase tracking-tight">
                        <span>View Team</span>
                        <ChevronLeft size={8} className="rotate-180" />
                    </div>
                </div>
            )}

            {/* Hover Tooltip */}
            {isHovered && ReactDOM.createPortal(
                <div
                    className="fixed w-52 bg-white rounded-lg shadow-2xl border-2 border-green-500 z-[9999999]"
                    style={{
                        left: `${tooltipPos.left}px`,
                        top: `${tooltipPos.top}px`
                    }}
                >
                    <div className="bg-green-500 text-white text-[9px] font-bold px-3 py-1.5 rounded-t-md uppercase tracking-wider">
                        Member Details
                    </div>
                    <div className="p-2 text-[9px] space-y-0.5">
                        <InfoRow label="sponsor id" value={user.sponsor_id} />
                        <InfoRow label="registered" value={user.joined_at ? new Date(user.joined_at).toLocaleString('en-GB') : 'N/A'} />
                        <InfoRow label="activated" value={user.activated_at ? new Date(user.activated_at).toLocaleString('en-GB') : 'Not Activated'} />
                        <InfoRow label="package (active) pv" value={user.pv || '0'} />
                        <InfoRow label="left total pv" value={`${user.left_pv || 0} (${user.left_count || 0})`} />
                        <InfoRow label="right total pv" value={`${user.right_pv || 0} (${user.right_count || 0})`} />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="flex items-center justify-between py-0.5 hover:bg-gray-50 rounded px-1">
        <span className="text-gray-400 font-semibold">{label}</span>
        <span className="text-gray-700 font-bold truncate ml-2 text-right max-w-[110px]">{value || '---'}</span>
    </div>
);

// ==================== EMPTY SLOT ====================
const EmptySlot = ({ position, node_id, side }) => {
    const { registerNode, unregisterNode } = useContext(TreeContext);
    const sideLower = String(side || position).toLowerCase();
    const isLeft = sideLower === 'left' || sideLower === 'l';
    const slotId = `empty-${node_id}-${position}`;
    const myRef = useRef(null);

    useLayoutEffect(() => {
        if (myRef.current) {
            registerNode(slotId, myRef, position, 999, true);
        }
        return () => unregisterNode(slotId);
    }, [slotId, position, registerNode, unregisterNode]);

    return (
        <div ref={myRef}
            className={`rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all
                ${isLeft ? 'border-blue-200 bg-blue-50/40' : 'border-purple-200 bg-purple-50/40'}`}
            style={{ width: '200px', height: '80px' }}
        >
            <div className={`w-8 h-8 rounded-full border-2 border-dashed flex items-center justify-center mb-1
                ${isLeft ? 'border-blue-300 text-blue-300' : 'border-purple-300 text-purple-300'}`}>
                <User size={14} />
            </div>
            <span className={`text-[9px] font-bold uppercase tracking-wider ${isLeft ? 'text-blue-300' : 'text-purple-300'}`}>
                {position === 'left' ? 'Left' : 'Right'} Position
            </span>
        </div>
    );
};

// ==================== TREE NODE ====================
const TreeNode = ({ node, level = 0, maxLevel = 50, position = 'root', side, onUserClick }) => {
    const { registerNode, unregisterNode } = useContext(TreeContext);
    const cardRef = useRef(null);

    const hasLeft = node?.left;
    const hasRight = node?.right;
    const showChildren = level < maxLevel;

    const currentSide = level === 0 ? undefined : (side || position);

    useLayoutEffect(() => {
        if (cardRef.current) {
            registerNode(node.id, cardRef, position, level, true);
        }
        return () => unregisterNode(node.id);
    }, [node.id, position, level, registerNode, unregisterNode]);

    if (level > maxLevel || !node) return null;

    return (
        <div className="flex flex-col items-center relative">
            {/* Current Node */}
            <div className="relative mb-10">
                <UserCard cardRef={cardRef} user={node} isRoot={level === 0} position={position} side={currentSide} onUserClick={onUserClick} />
            </div>

            {/* Children */}
            {showChildren && (
                <div className="flex gap-6 sm:gap-10 items-start relative">
                    <div className="flex flex-col items-center">
                        {hasLeft ? (
                            <TreeNode node={node.left} level={level + 1} maxLevel={maxLevel} position="left" side={currentSide || 'left'} onUserClick={onUserClick} />
                        ) : (
                            <EmptySlot node_id={node.id} position="left" side={currentSide || 'left'} />
                        )}
                    </div>
                    <div className="flex flex-col items-center">
                        {hasRight ? (
                            <TreeNode node={node.right} level={level + 1} maxLevel={maxLevel} position="right" side={currentSide || 'right'} onUserClick={onUserClick} />
                        ) : (
                            <EmptySlot node_id={node.id} position="right" side={currentSide || 'right'} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== GLOBAL CONNECTORS (Clean gray lines) ====================
const GlobalConnectors = ({ treeData, contentRef, containerRef }) => {
    const { nodes } = useContext(TreeContext);
    const [paths, setPaths] = useState([]);
    const [dimensions, setDimensions] = useState({ width: '100%', height: '100%' });
    const [isReady, setIsReady] = useState(false);

    const calculatePaths = useCallback(() => {
        if (!contentRef.current || !treeData) return false;

        const container = contentRef.current;
        const contentRect = container.getBoundingClientRect();
        if (contentRect.width === 0 || contentRect.height === 0) return false;

        const nodeCount = Object.keys(nodes.current).length;
        if (nodeCount === 0) return false;

        const scrollWidth = container.scrollWidth;
        const scrollHeight = container.scrollHeight;

        setDimensions({ width: scrollWidth, height: scrollHeight });

        const newPaths = [];

        const traverse = (node) => {
            if (!node) return;
            const nodeInfo = nodes.current[node.id];

            let parentX, parentY;
            let parentReady = false;

            if (nodeInfo && nodeInfo.ref.current) {
                const pRect = nodeInfo.ref.current.getBoundingClientRect();
                parentX = pRect.left - contentRect.left + pRect.width / 2;
                parentY = pRect.bottom - contentRect.top;
                parentReady = true;
            }

            const processChild = (childNode, childPos) => {
                const childId = childNode ? childNode.id : `empty-${node.id}-${childPos}`;
                const childInfo = nodes.current[childId];

                if (parentReady && childInfo && childInfo.ref.current) {
                    const cRect = childInfo.ref.current.getBoundingClientRect();
                    const childX = cRect.left - contentRect.left + cRect.width / 2;
                    const childY = cRect.top - contentRect.top;

                    const midY = parentY + (childY - parentY) / 2;

                    newPaths.push({
                        d: `M ${parentX} ${parentY} L ${parentX} ${midY} L ${childX} ${midY} L ${childX} ${childY}`,
                        color: '#d1d5db',
                        endX: childX,
                        endY: childY
                    });
                }

                if (childNode) traverse(childNode);
            };

            processChild(node.left, 'left');
            processChild(node.right, 'right');
        };

        traverse(treeData);
        setPaths(newPaths);
        return newPaths.length > 0;
    }, [treeData, contentRef, nodes]);

    useEffect(() => {
        if (!treeData) return;

        let attempts = 0;
        const maxAttempts = 100;

        const tryCalculate = () => {
            const success = calculatePaths();
            attempts++;

            if (success) {
                setIsReady(true);
            } else if (attempts < maxAttempts) {
                setTimeout(tryCalculate, 100);
            }
        };

        const initialTimer = setTimeout(tryCalculate, 50);
        return () => clearTimeout(initialTimer);
    }, [treeData, calculatePaths]);

    useEffect(() => {
        if (!isReady) return;

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                calculatePaths();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isReady, calculatePaths]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const update = () => calculatePaths();

        const contentElement = contentRef.current;
        const scrollElement = containerRef?.current;

        update();

        requestAnimationFrame(() => {
            requestAnimationFrame(update);
        });

        window.addEventListener('resize', update);
        window.addEventListener('load', update);

        if (scrollElement) {
            scrollElement.addEventListener('scroll', update);
        }

        const resizeObserver = new ResizeObserver(update);
        const mutationObserver = new MutationObserver(update);

        if (contentElement) {
            resizeObserver.observe(contentElement);
            mutationObserver.observe(contentElement, {
                childList: true,
                subtree: true,
                attributes: true
            });
        }

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('load', update);
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', update);
            }
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [calculatePaths, contentRef, containerRef, isReady]);

    return (
        <svg
            className="absolute top-0 left-0 pointer-events-none overflow-visible"
            style={{
                zIndex: 5,
                width: dimensions.width,
                height: dimensions.height
            }}
        >
            {paths.map((path, i) => (
                <React.Fragment key={i}>
                    <path
                        d={path.d}
                        fill="none"
                        stroke={path.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <circle cx={path.endX} cy={path.endY} r="3" fill={path.color} />
                </React.Fragment>
            ))}
        </svg>
    );
};

// ==================== BREADCRUMB NAVIGATION ====================
const BreadcrumbNav = ({ trail, onNavigate }) => {
    if (!trail || trail.length <= 1) return null;

    return (
        <div className="flex items-center gap-1 flex-wrap">
            {trail.map((item, idx) => (
                <React.Fragment key={item.id}>
                    {idx > 0 && (
                        <ChevronLeft size={12} className="text-gray-300 shrink-0" />
                    )}
                    <button
                        onClick={() => onNavigate(idx)}
                        className={`text-xs font-semibold px-2 py-1 rounded-lg transition-all ${idx === trail.length - 1
                            ? 'bg-blue-100 text-blue-700 cursor-default'
                            : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                        disabled={idx === trail.length - 1}
                    >
                        {item.name}
                    </button>
                </React.Fragment>
            ))}
        </div>
    );
};

// ==================== MAIN COMPONENT ====================
const MyDownlines = () => {
    const [treeData, setTreeData] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [maxDepth, setMaxDepth] = useState(2);
    const [timerInfo, setTimerInfo] = useState(null);
    const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const nodesRef = useRef({});

    // Navigation trail: [{id, name, userId}] — tracks clicked user path
    const [navTrail, setNavTrail] = useState([]);
    // Currently viewed user ID (null = self/root)
    const [viewingUserId, setViewingUserId] = useState(null);

    const registerNode = useCallback((id, ref, position, level, expanded) => {
        nodesRef.current[id] = { ref, position, level, expanded };
    }, []);

    const unregisterNode = useCallback((id) => {
        delete nodesRef.current[id];
    }, []);

    // Fetch timer info from API
    const fetchTimerInfo = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/downline_timer_info.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setTimerInfo(data);
                setCountdown({
                    hours: data.timer.hours,
                    minutes: data.timer.minutes,
                    seconds: data.timer.seconds
                });
            }
        } catch (error) {
            console.error('Failed to fetch timer info:', error);
        }
    };

    const fetchTree = useCallback(async (userId = null) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            // If viewing a specific user's downline, pass their ID
            let url = `${API_BASE_URL}/user/my_downlines.php?max_depth=${maxDepth}`;
            if (userId) {
                url += `&user_id=${userId}`;
            }

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                nodesRef.current = {};
                setTreeData(mapResponseToTree(data.tree));
                setStats(data.stats);
            }
        } catch (error) {
            console.error('Failed to fetch tree:', error);
        } finally {
            setLoading(false);
        }
    }, [maxDepth]);

    // Handle clicking on a downline user — drill down into their tree
    const handleUserClick = useCallback((user) => {
        // Add clicked user to navigation trail
        setNavTrail(prev => [...prev, { id: user.id, name: user.name, userId: user.user_id }]);
        setViewingUserId(user.id);
        fetchTree(user.id);
    }, [fetchTree]);

    // Handle breadcrumb navigation — go back to a previous user
    const handleBreadcrumbNavigate = useCallback((index) => {
        if (index === 0) {
            // Go back to self (root)
            setNavTrail([navTrail[0]]);
            setViewingUserId(null);
            fetchTree(null);
        } else {
            // Go to that specific user
            const targetItem = navTrail[index];
            setNavTrail(navTrail.slice(0, index + 1));
            setViewingUserId(targetItem.id);
            fetchTree(targetItem.id);
        }
    }, [navTrail, fetchTree]);

    // Countdown timer effect
    useEffect(() => {
        const interval = setInterval(() => {
            setCountdown(prev => {
                let { hours, minutes, seconds } = prev;

                if (seconds > 0) {
                    seconds--;
                } else if (minutes > 0) {
                    minutes--;
                    seconds = 59;
                } else if (hours > 0) {
                    hours--;
                    minutes = 59;
                    seconds = 59;
                } else {
                    fetchTimerInfo();
                    return prev;
                }

                return { hours, minutes, seconds };
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Initial load — fetch tree and set first trail entry
    useEffect(() => {
        fetchTree();
        fetchTimerInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxDepth]);

    // Set initial trail entry when tree data loads for first time
    useEffect(() => {
        if (treeData && navTrail.length === 0) {
            setNavTrail([{ id: treeData.id, name: treeData.name, userId: treeData.user_id }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData]);

    return (
        <Layout>
            <TreeContext.Provider value={{ nodes: nodesRef, registerNode, unregisterNode }}>
                <div className="h-screen flex flex-col bg-gray-50/80 overflow-hidden">
                    {/* Header Section */}
                    <div className="flex-shrink-0 p-3 sm:p-4">
                        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Network Tree</h1>
                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Click on any member to explore their downline</p>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                                <select
                                    value={maxDepth}
                                    onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                                    className="flex-1 sm:flex-none px-2 sm:px-3 py-2 text-[10px] sm:text-xs font-bold border border-gray-200 rounded-xl bg-white shadow-sm transition-all"
                                >
                                    <option value={2}>3 Levels</option>
                                    <option value={4}>5 Levels</option>
                                    <option value={9}>10 Levels</option>
                                    <option value={50}>Deep View</option>
                                </select>
                                <button onClick={() => fetchTree(viewingUserId)} className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white text-[10px] sm:text-xs font-bold rounded-xl hover:bg-blue-700 shadow-md transition-all">
                                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                                    <span className="hidden xs:inline">Refresh</span>
                                </button>
                            </div>
                        </div>

                        {/* Stats and Timer Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
                            {/* Unified Network Stats Card */}
                            {stats && (
                                <div className="h-full bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-4 sm:p-5 shadow-xl text-white flex flex-col justify-between relative overflow-hidden group">
                                    <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <TrendingUp size={120} />
                                    </div>

                                    <div className="flex items-start justify-between relative z-10 gap-2">
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-md rounded-xl sm:rounded-2xl flex items-center justify-center shadow-inner">
                                                <TrendingUp size={24} className="text-white" />
                                            </div>
                                            <div>
                                                <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/80">TOTAL PV</p>
                                                <p className="text-xl sm:text-3xl font-black tabular-nums">{Number(stats.total_pv || 0).toLocaleString()} <span className="text-xs font-bold text-blue-200">PV</span></p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/80">USERS</p>
                                            <p className="text-lg sm:text-2xl font-black tabular-nums">{stats.total_downlines || 0}</p>
                                        </div>
                                    </div>

                                    <div className="mt-4 sm:mt-6 grid grid-cols-2 gap-2 relative z-10">
                                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3 border border-white/10">
                                            <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">LEFT</p>
                                            <div className="flex justify-between items-end">
                                                <p className="text-sm sm:text-xl font-black">{Number(stats.left_pv || 0).toLocaleString()}<span className="text-[8px] font-normal ml-0.5">PV</span></p>
                                                <p className="text-[10px] font-bold text-blue-200/50">#{stats.left_count || 0}</p>
                                            </div>
                                        </div>
                                        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3 border border-white/10">
                                            <p className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">RIGHT</p>
                                            <div className="flex justify-between items-end">
                                                <p className="text-sm sm:text-xl font-black">{Number(stats.right_pv || 0).toLocaleString()}<span className="text-[8px] font-normal ml-0.5">PV</span></p>
                                                <p className="text-[10px] font-bold text-blue-200/50">#{stats.right_count || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 12-Hour Timer */}
                            {timerInfo && (
                                <div className="h-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-3 sm:p-4 shadow-lg border border-slate-700 flex flex-col justify-center">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                                        <div className="flex items-center gap-3 w-full sm:w-auto">
                                            <div className="relative">
                                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                                                    <Timer size={20} className="animate-pulse" />
                                                </div>
                                                <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-slate-900 animate-ping"></div>
                                            </div>
                                            <div>
                                                <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">Next Payout</p>
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-xl sm:text-2xl font-black text-white tabular-nums">{String(countdown.hours).padStart(2, '0')}</span>
                                                    <span className="text-lg text-amber-400 animate-pulse">:</span>
                                                    <span className="text-xl sm:text-2xl font-black text-white tabular-nums">{String(countdown.minutes).padStart(2, '0')}</span>
                                                    <span className="text-lg text-amber-400 animate-pulse">:</span>
                                                    <span className="text-xl sm:text-2xl font-black text-white tabular-nums">{String(countdown.seconds).padStart(2, '0')}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-2 w-full sm:w-auto justify-between sm:justify-end flex-wrap">
                                            <div className="text-center px-2 py-1.5 sm:px-3 sm:py-2 bg-blue-600/10 rounded-xl border border-blue-500/20">
                                                <p className="text-[8px] text-blue-300 font-bold uppercase tracking-wider">L PV</p>
                                                <p className="text-sm sm:text-lg font-black text-blue-400">{timerInfo.extreme_pv?.left?.pv?.toFixed(0) || 0}</p>
                                            </div>
                                            <div className="text-center px-2 py-1.5 sm:px-3 sm:py-2 bg-emerald-600/20 rounded-xl border border-emerald-500/30">
                                                <p className="text-[8px] text-emerald-300 font-bold uppercase tracking-wider flex items-center justify-center gap-1">MATCH</p>
                                                <p className="text-sm sm:text-lg font-black text-emerald-400">{timerInfo.matching?.matched_pv?.toFixed(0) || 0}</p>
                                            </div>
                                            <div className="text-center px-2 py-1.5 sm:px-3 sm:py-2 bg-purple-600/10 rounded-xl border border-purple-500/20">
                                                <p className="text-[8px] text-purple-300 font-bold uppercase tracking-wider">R PV</p>
                                                <p className="text-sm sm:text-lg font-black text-purple-400">{timerInfo.extreme_pv?.right?.pv?.toFixed(0) || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Legend Row */}
                        <div className="hidden sm:flex items-center justify-between bg-white py-2 px-4 rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
                            <div className="flex gap-4 sm:gap-6 text-[9px] sm:text-[10px] font-bold text-gray-500 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-amber-400 to-amber-500" /> Leader
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600" /> Left Side
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-purple-500 to-purple-600" /> Right Side
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tree Container */}
                    <div className="flex-1 px-4 pb-4 overflow-hidden">
                        <div
                            ref={containerRef}
                            className="bg-white rounded-2xl shadow-lg border border-gray-200 h-full relative overflow-auto scroll-smooth"
                        >
                            {/* Sticky Breadcrumbs in Body */}
                            {navTrail && navTrail.length > 1 && (
                                <div className="sticky top-4 left-0 right-0 z-[60] flex justify-center pointer-events-none px-4">
                                    <div className="pointer-events-auto bg-white/80 backdrop-blur-md px-4 py-2 rounded-2xl shadow-xl border border-blue-50/50 flex items-center gap-2 max-w-full">
                                        <BreadcrumbNav trail={navTrail} onNavigate={handleBreadcrumbNavigate} />
                                    </div>
                                </div>
                            )}
                            <div
                                ref={contentRef}
                                className="tree-content min-w-max p-12 sm:p-20 flex justify-center items-start min-h-full relative overflow-visible"
                            >
                                {/* Subtle grid pattern */}
                                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                                    style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-20 w-full">
                                        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                        <p className="mt-4 text-gray-500 text-sm font-semibold animate-pulse">Building your network...</p>
                                    </div>
                                ) : treeData ? (
                                    <>
                                        <div className="relative z-10">
                                            <TreeNode node={treeData} level={0} maxLevel={maxDepth} onUserClick={handleUserClick} />
                                        </div>
                                        <GlobalConnectors
                                            key={`connectors-${treeData?.id || Date.now()}`}
                                            treeData={treeData}
                                            contentRef={contentRef}
                                            containerRef={containerRef}
                                        />
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 w-full">
                                        <Users size={48} className="mb-4 opacity-20" />
                                        <p className="font-bold">No network data found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </TreeContext.Provider>
        </Layout>
    );
};

export default MyDownlines;
